import { useSelector } from "react-redux";
import { useFormContext } from "react-hook-form";
import useToastMessage from "repoV2/components/common/Toast/useToastMessage";
import {
    FORM_KEYS,
    multiple_users_helpers_keys,
} from "repoV2/features/Common/modules/FormData/FormData.constants";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { booking_initial_user_id } from "repoV2/features/CheckoutPages/constants/CheckoutPages.constants";
import { IMultipleUsersHelpers } from "repoV2/features/Common/modules/FormData/interfaces/MultipleUserHelpers.interfaces";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import useGetBillingStateHelpers from "repoV2/features/CheckoutPages/utils/useGetBillingStateHelpers";
import {
    getCountryDataBasedOnTimezone,
    getUserTimezone,
} from "repoV2/features/Common/modules/Timezone/Timezone.utils";
import { INDIA_COUNTRY_DATA } from "repoV2/utils/common/location/countriesData";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { getUserFormFieldName } from "../UserDetailsFormGroup.utils";
import {
    checkout_form,
    checkout_form_error_strings,
} from "../../../../CheckoutForm/CheckoutForm.constants";
import { getUserId } from "./getUserId";
import {
    useCouponCodeFormGroup,
    useRemoveCouponCode,
} from "../../CouponCodeFormGroup/CouponCodeFormGroup.utils";
import { COUPON_TYPES } from "../../CouponCodeFormGroup/CouponCodeFormGroup.constants";

const {
    user_details: { field_names },
} = checkout_form;

const { submit_all } = checkout_form_error_strings;

const useMultipleUserDetails = () => {
    const {
        listingUuid,
        listingType,
        enableMultipleUserCountFlow,
        communityUuid,
    } = useCheckoutBasicDetailsContext();
    const { getValues, setValue, handleSubmit, unregister } = useFormContext();
    const { setFormData, removeFormData } = useSetFormData();
    const { notify, toast_type_keys } = useToastMessage();
    const { isBillingStateDisabled } = useGetBillingStateHelpers();
    const { onRemoveCouponCodeClick } = useRemoveCouponCode();
    const { isCouponCodeApplied, appliedCouponType } = useCouponCodeFormGroup();
    const orderBumpsCount = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.orderBumpsDetails
                ?.data?.orderBumpsCount
    );

    // We do not show the add new user button, (hide multi user flow), incase
    // we have order bumps,
    // or listing is of appointments type
    // or listing is of telegram community type
    // or it is a bonus offering (in this case we have a value in communityUuid)
    const hideAddNewUserButton =
        orderBumpsCount > 0 ||
        listingType === LISTING_TYPES.APPOINTMENTS ||
        listingType === LISTING_TYPES.TELEGRAM ||
        listingType === LISTING_TYPES.MERCHANDISE ||
        !checkIsStringEmpty(communityUuid);

    const userDetails = useGetFormData<{ [userId: string]: IUserDetails }>({
        key: `${FORM_KEYS.user_details}`,
    });

    const allUserIds = useGetFormData<IMultipleUsersHelpers["allUserIds"]>({
        key: `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
    });

    const preEditUserDetails = useGetFormData<
        IMultipleUsersHelpers["preEditUserDetails"]
    >({
        key: `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.pre_edit_user_details}`,
    });

    // currentUserId will be the id of the new user about to be created in
    // case of the add new user flow, and it will be the id of the user to
    // be edit in case of editing the user details
    const currentUserIdBase = allUserIds[allUserIds.length - 1].userIdBase + 1;
    const currentUserId =
        preEditUserDetails.userIdToEdit || getUserId(currentUserIdBase);

    // Incase there is only one user, then the user id of that user is calculated
    // from here. Suppose there were four users, user1, user2, user3 and user4, and
    // user1, user2, user are deleted, the user4 will be the id of the single remaining user.
    const singleUserId =
        allUserIds.length === 1
            ? allUserIds[0].userId
            : booking_initial_user_id;

    const handleUserDetailsFormClose = () => {
        setFormData(
            `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.is_user_details_form_open}`,
            false
        );
    };

    const handleUserDetailsFormOpen = () => {
        setFormData(
            `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.is_user_details_form_open}`,
            true
        );
    };

    /**
     * This function runs when we click on edit icon on the
     * any card of multiple users
     * @param userIdToEdit User id  of the user to edit
     */
    const handleEditUser = (userIdToEdit: string) => {
        const data = getValues() || {};
        const preEditUserRefsDetails: { [key: string]: string } = {};

        // Filter out the keys from the data containing the data of the required user
        Object.keys(data).forEach(key => {
            if (key.includes(userIdToEdit)) {
                preEditUserRefsDetails[key] = data[key];
            }
        });

        // Get the redux data for the user
        const preEditUserReduxDetails = userDetails?.[userIdToEdit] || {};

        // Set the user id that is being edited to this state.
        // This will be used to set the userId of the edit modal form which
        // will allow the form to be prefilled by the user's data
        // Set the pre edit user details state
        setFormData(
            `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.pre_edit_user_details}`,
            {
                userIdToEdit,
                userRefsData: preEditUserRefsDetails,
                userReduxData: preEditUserReduxDetails,
            }
        );

        // Open the user details modal
        handleUserDetailsFormOpen();
    };

    /**
     * This function runs when we click on the delete icon
     * of any card from the multiple users cards
     * @param userIdToRemove User id to remove
     */
    const handleRemoveUser = (userIdToRemove: string) => {
        // Remove the userId from allUserIds for the user which is being removed
        const updatedUserIds = allUserIds.filter(
            userId => userId.userId !== userIdToRemove
        );

        if (updatedUserIds.length !== allUserIds.length) {
            const data = getValues() || {};

            // Filter out the keys of the user that is being removed
            const userDetailsKeysToDelete = Object.keys(data).filter(key =>
                key.includes(userIdToRemove)
            );

            // Incase file upload custom-question is there, then an extra field is present,
            // containing the file name which needs to be set as undefined.
            // This is done to keep the data clean and truthful.
            // Explanation - 'unregister' only removes the fields that were given to a form field
            // or input. Since the keys ending with '-file' are not assigned to any field and only
            // contain the upload file preview data, we have to set it as undefined or null, else
            // upon deleting user data of lets say user 2, user2_[file_question_uuid]-file key will
            // still remain in the form data
            // Filter out the file upload fields for the user
            // TODO: find a better way to do this than 'endsWith("-file")'
            const userDetailsFileKeys = userDetailsKeysToDelete.filter(key =>
                key.endsWith("-file")
            );

            // unregister the keys from 'react-hook-form' state
            unregister(userDetailsKeysToDelete);

            // remove the data for this user from redux state
            removeFormData(`${FORM_KEYS.user_details}.${userIdToRemove}`);

            // set the file upload name keys as undefined
            userDetailsFileKeys.forEach(key => {
                setValue(key, undefined);
            });

            // update the new value for allUserIds
            setFormData(
                `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
                [...updatedUserIds]
            );
        }
    };

    /**
     * This is the function called when we click to add a new user.
     */
    const handleAddNewUser = () => {
        // First, we fetch the timezone of the user to get its default country.
        const timezone = getUserTimezone();
        const { dial_code: defaultDialCode = INDIA_COUNTRY_DATA.dial_code } =
            getCountryDataBasedOnTimezone({ timezone }) || {};

        const firstUserDetails = userDetails[singleUserId] || {};

        // Generate the newUserIdBase, to get the newUserId
        // Note that allUserIds will never be of length less than 1, as
        // default value is set in redux
        const newUserIdBase = allUserIds[allUserIds.length - 1].userIdBase + 1;
        const newUserId = getUserId(newUserIdBase);

        // generate the fieldName according to the user
        const countryCodeFieldName = getUserFormFieldName(
            newUserId,
            field_names.country_code
        );
        const billingStateFieldName = getUserFormFieldName(
            newUserId,
            field_names.billing_state
        );

        // Set the value for country_code in refs state as well as redux state for
        // this new user. This is necessary to show the country code dropdown pre-filled.
        setValue(
            countryCodeFieldName,
            firstUserDetails.country_code || defaultDialCode
        );

        // If billing state is disabled, then we also pre-fill this field as well
        if (isBillingStateDisabled && firstUserDetails.billing_state?.value) {
            setValue(billingStateFieldName, firstUserDetails.billing_state);
        }
        setFormData(`${FORM_KEYS.user_details}.${newUserId}`, {
            [field_names.country_code]:
                firstUserDetails.country_code || defaultDialCode,
            ...(isBillingStateDisabled &&
                firstUserDetails.billing_state?.value && {
                    [field_names.billing_state]: firstUserDetails.billing_state,
                }),
        });

        // Open the user details modal
        handleUserDetailsFormOpen();
    };

    const handleSaveUserDetailsModalClick = async () => {
        handleSubmit(
            () => {
                handleUserDetailsFormClose();
                if (!preEditUserDetails.userIdToEdit) {
                    const newUserIdBase =
                        allUserIds[allUserIds.length - 1].userIdBase + 1;
                    setFormData(
                        `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
                        [
                            ...allUserIds,
                            {
                                userId: getUserId(newUserIdBase),
                                userIdBase: newUserIdBase,
                            },
                        ]
                    );

                    // Loyalty Coupons does'nt exist for multiple users, so if a
                    // coupon was already applied of loyalty type, then we remove it
                    if (
                        isCouponCodeApplied &&
                        appliedCouponType === COUPON_TYPES.LOYALTY_COUPON
                    ) {
                        onRemoveCouponCodeClick();
                    }
                } else {
                    setFormData(
                        `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.pre_edit_user_details}`,
                        {
                            userIdToEdit: "",
                            userRefsData: {},
                            userReduxData: {},
                        }
                    );
                }
            },
            () => {
                notify(submit_all, {
                    type: toast_type_keys.error,
                });
            }
        )();
    };

    const handleCloseUserDetailsModalClick = () => {
        // If we are adding a new user, and close the modal without saving,
        // then following block will run
        if (!preEditUserDetails.userIdToEdit) {
            const data = getValues() || {};

            // Filter out the keys that were registered for this particular user.
            const userDetailsKeysToDelete = Object.keys(data).filter(key =>
                key.includes(currentUserId)
            );

            // Incase file upload custom-question is there, then an extra field is
            // present, containing the file name which needs to be set as undefined.
            // Filter out the file upload fields for the user
            const userDetailsFileKeys = userDetailsKeysToDelete.filter(key =>
                key.endsWith("-file")
            );

            // unregister the user fields
            unregister(userDetailsKeysToDelete);

            // set the file upload name keys as undefined
            userDetailsFileKeys.forEach(key => {
                setValue(key, undefined);
            });

            // Remove the data for this user stored in redux
            removeFormData(`${FORM_KEYS.user_details}.${currentUserId}`);
        } else {
            // Destructure the user refs data, and the redux state data
            // of the user that was before edit
            const { userRefsData = {}, userReduxData = {} } =
                preEditUserDetails || {};

            // Update the react-hook-form states for the user with the pre edit states
            Object.keys(userRefsData).forEach(key => {
                setValue(key, userRefsData[key]);
            });

            // Update the redux state for the user with pre edit states
            setFormData(
                `${FORM_KEYS.user_details}.${currentUserId}`,
                userReduxData
            );

            setFormData(
                `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.pre_edit_user_details}`,
                {
                    userIdToEdit: "",
                    userRefsData: {},
                    userReduxData: {},
                }
            );
        }
        // Close the modal
        handleUserDetailsFormClose();
    };

    return {
        hideAddNewUserButton,
        handleUserDetailsFormClose,
        handleUserDetailsFormOpen,
        handleEditUser,
        handleRemoveUser,
        handleAddNewUser,
        handleSaveUserDetailsModalClick,
        handleCloseUserDetailsModalClick,
        currentUserIdBase,
        currentUserId,
        singleUserId,
        enableMultipleUserCountFlow,
    };
};

export default useMultipleUserDetails;
